import { Component, OnInit } from '@angular/core';
import { People, PeopleAdapter } from '../models/people';
import { Observable, Subject, catchError, combineLatest, debounceTime, map, of, startWith, switchMap, tap } from 'rxjs';
import { PeoplesService } from '../services/peoples.service';
import { CommonModule } from '@angular/common';
import { PeopleAddModalComponent } from '../people-add-modal/people-add-modal.component';
import { TeamAssignationModalComponent } from '../team-assignation-modal/team-assignation-modal.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { RouterModule } from '@angular/router';
import { HideAfterDirective } from '../directives/hide-after.directive';
import { ITeamAllocation } from '../_interfaces/teamAllocation';
import { TranslatePipe } from '@ngx-translate/core';
import { getTeamColor as getTeamColorUtil } from '../_utils/team.util';
import { AvatarComponent } from '../_utils/components/avatar/avatar.component';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { IProfileSearch } from '../_interfaces/profile-search';
import { Team, TeamAdapter } from '../models/team';
import { Role, RoleAdapter } from '../models/role';
import { RolesService } from '../services/roles.service';
import { TeamsService } from '../services/teams.service';
import { PeopleStatusComponent } from "../_utils/components/people-status/people-status.component";
import { ToastService } from '../services/toast.service';
import { PeopleStatusIconComponent } from '../_utils/components/people-status-icon/people-status-icon.component';
import { getPersonHiringStatusClass, getPersonStatusIconAndMessage, getAllocationRateClass } from '../_utils/people_util';



@Component({
  selector: 'app-people-list',
  standalone: true,
  imports: [PeopleAddModalComponent,
            CommonModule,
            ConfirmDialogComponent,
            TeamAssignationModalComponent,
            RouterModule,
            HideAfterDirective,
            TranslatePipe,
            AvatarComponent,
            ReactiveFormsModule,
            PeopleStatusComponent,
            PeopleStatusIconComponent],
  templateUrl: './people-list.component.html',
  styleUrl: './people-list.component.scss'
})

export class PeopleListComponent {

  search = this.fb.nonNullable.group({
      name: [''],
      roleId: [''],
      teamId: [''],
      status: ['']
    });

  showEditPersonModal: boolean = false;
  showTeamAssignationModal: boolean = false;
  showConfirmDialog: boolean = false;
  personToDelete: People |null = null;
  personToEdit: People | null = null;
  teamsFilter$: Observable<Team[]> = this.fetchTeams(); 
  rolesFilter$: Observable<Role[]> = this.fetchRoles();

  errorMessage: string | null = null;

  private reloadPeoples$ = new Subject<void>();
  peoples$: Observable<People[]> = this.getPeoples();

  isLoading = false;

  public getPersonHiringStatusClass = getPersonHiringStatusClass;
  public getPersonStatusIconAndMessage = getPersonStatusIconAndMessage;

  constructor(
    private peoplesService: PeoplesService,
    private peopleAdapter: PeopleAdapter,
    private roleService: RolesService,
    private roleAdapter: RoleAdapter,
    private teamService: TeamsService,
    private teamAdapter: TeamAdapter,
    private fb: FormBuilder,
    private toastService: ToastService
  ) { }

  private getPeoples(): Observable<People[]> {

    const search$ = combineLatest([
      this.search.controls.name.valueChanges.pipe(startWith('')),
      this.search.controls.roleId.valueChanges.pipe(startWith('')),
      this.search.controls.teamId.valueChanges.pipe(startWith('')),
      this.search.controls.status.valueChanges.pipe(startWith('')),
      this.reloadPeoples$.pipe(startWith(''))
    ]).pipe(
      debounceTime(300),
      map(([name, roleId, teamId, status, _]) => {
        const filters = {name, roleId, teamId, status};
        return filters;
    }),
    );

    return search$.pipe(
    tap(() => this.isLoading = true), // loader ON
    switchMap((params) =>
      this.fetchPeoples(params).pipe(
        tap(() => this.isLoading = false), // loader OFF 
        catchError((error) => {
          this.isLoading = false; // loader OFF in case of error
          this.errorMessage = 'Error during peoples fetch : ' + error.message;
          return of([]);
        })
      )
    )
  );
  }

  private fetchPeoples(search: IProfileSearch): Observable<People[]> {
    return this.peoplesService.get_peoples(search).pipe(
      map((data: any[]) => data.map(item => this.peopleAdapter.adapt(item))),
      catchError((error: any) => {
        this.errorMessage = 'Error during peoples fetch : ' + error.message;
        return of([]); // return empty table to avoid binding error
      })
    );
  }

  private fetchRoles():Observable<Role[]>{
    return this.roleService.getRoles().pipe(
      map((data: any[]) => data.map(item => this.roleAdapter.adapt(item))),
      catchError((error: any) => {
        this.errorMessage = 'Error during roles fetch : ' + error.message;
        return of([]); // return empty table to avoid binding error
      })
    );
  }

  private fetchTeams():Observable<Team[]>{
    return this.teamService.getTeams().pipe(
      map((data: any[]) => data.map(item => this.teamAdapter.adapt(item))),
      catchError((error: any) => {
        this.errorMessage = 'Error during teams fetch : ' + error.message;
        return of([]); // return empty table to avoid binding error
      })
    );
  }

  openEditPersonModal(person: People): void {
    this.personToEdit = { ...person };
    this.showEditPersonModal = true;
  }

  openAddPersonModal(): void {
    this.personToEdit = null
    this.showEditPersonModal = true;
  }

  openTeamAssignationModal(person: People): void {
    this.personToEdit = person;
    this.showTeamAssignationModal = true;
  }


  addOrEditPerson(newPerson: People | undefined) {
    if (newPerson) { // if the user has filled the form and clicked on save

      let updateCreatePeople$: Observable<People>;

      if (this.personToEdit && this.personToEdit.id != 0) { // edit an existing people
        updateCreatePeople$ = this.peoplesService.update_people(this.peopleAdapter.adaptToApi(newPerson));
      } else { // add a new people
        updateCreatePeople$ = this.peoplesService.add_people(this.peopleAdapter.adaptToApi(newPerson));
      }

      updateCreatePeople$.subscribe({
        next: (person: People) => {
          this.reloadPeoples$.next();
          this.showEditPersonModal = false;
          this.personToEdit = null;
          this.toastService.showSuccess('COMMON.UPDATE_SUCCESS');
        },
        error: (err) => {
          console.error('Erreur lors de l\'ajout/édition de la personne:', err);
          this.errorMessage = err.message;
          this.toastService.showError('COMMON.SAVE_ERROR');
        }
      });
    } else { // if the user has clicked on cancel
      this.showEditPersonModal = false;
      this.personToEdit = null
    }
  }

  editTeamAssignation(updatedTeams: ITeamAllocation[] | undefined): void {
    

    if (updatedTeams && this.personToEdit) {
      this.reloadPeoples$.next();
    } 
    this.personToEdit = null
    this.showTeamAssignationModal = false;
  }

  confirmDelete(person: People): void {
    this.personToDelete = person;
    this.showConfirmDialog = true;
  }

  handleConfirmDelete(confirm: boolean): void {
    if (confirm) {
      this.peoplesService.delete_people(this!.personToDelete!.id).subscribe({
        next: () => {
          this.reloadPeoples$.next();
          this.toastService.showSuccess('COMMON.DELETE_SUCCESS');
          
        },
        error: (err) => {
          this.toastService.showError('COMMON.DELETE_ERROR');
          this.errorMessage = err.message;
        },
    });
    }
    this.showConfirmDialog = false;
    this.personToDelete = null;
  }

  public getTeamColor(name: string): string {
    return getTeamColorUtil(name);
  }

  resetSearchField(controlName: string): void {
    this.search.get(controlName)?.setValue('');   
  }

  getAllocationRateClass(person: People): string {
    return getAllocationRateClass(person);
  }
}


